import { SITE_DOMAIN, SITE_NAME } from './site';

export const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Troubleshooting', to: '/troubleshooting' },
  { label: 'Updates', to: '/updates' },
  { label: 'Helps Services', to: '/helps-services' },
  { label: 'Guides', to: '/guides' },
  { label: 'FAQs', to: '/faqs' },
  {
    label: 'Company',
    to: '/about-us',
    children: [
      { label: 'About Us', to: '/about-us' },
      { label: 'Contact Us', to: '/contact-us' },
      { label: 'Refund Policy', to: '/refund-policy' },
      { label: 'Privacy Policy', to: '/privacy-policy' },
    ],
  },
];

export const POLICY_LINKS = [
  { label: 'Privacy Policy', to: '/privacy-policy' },
  { label: 'Terms & Conditions', to: '/terms-and-conditions' },
  { label: 'Refund Policy', to: '/refund-policy' },
  { label: 'Cookie Policy', to: '/cookie-policy' },
  { label: 'Disclaimer', to: '/disclaimer' },
];

export const HERO_POINTS = ['Sign-in & account help', 'Tree & record fixes', 'DNA kit questions', 'App & sync updates'];

export const STATS = [
  { value: '24/7', label: 'Live chat availability' },
  { value: '40+', label: 'Common issues documented' },
  { value: '12', label: 'Step-by-step guides' },
  { value: '0', label: 'Jargon — plain English only' },
];

export const SERVICES = [
  {
    icon: 'key',
    title: 'Sign-In & Account Access',
    text: 'Forgotten passwords, verification codes that never arrive, locked accounts, and duplicate accounts created by mistake.',
  },
  {
    icon: 'tree',
    title: 'Family Tree Problems',
    text: 'Duplicate people, wrong relationships, merged records that went sideways, and trees that load slowly or not at all.',
  },
  {
    icon: 'dna',
    title: 'DNA Kit & Results',
    text: 'Activating a kit, linking results to the right person, understanding matches, and what to expect while you wait.',
  },
  {
    icon: 'sync',
    title: 'App Updates & Syncing',
    text: 'Mobile app crashes after updates, changes that will not sync, and keeping desktop tree software in step with your online tree.',
  },
  {
    icon: 'search',
    title: 'Records & Search',
    text: 'Getting better search results, reading hints, attaching records correctly, and finding people the search keeps missing.',
  },
  {
    icon: 'photo',
    title: 'Photos & Media',
    text: 'Uploads that fail, blurry or sideways images, organising media, and downloading your photos and stories safely.',
  },
];

export const STEPS = [
  {
    title: 'Tell us what’s happening',
    text: 'Open live chat or send the contact form. Describe the problem in your own words — screenshots and error messages help.',
  },
  {
    title: 'We diagnose it with you',
    text: 'A technician walks through the likely causes, from browser settings to tree data, and confirms what is really going on.',
  },
  {
    title: 'Fix it — and keep it fixed',
    text: 'We guide you through the fix step by step, then share tips so the same issue does not come back after the next update.',
  },
];

export const WHY_US = [
  { icon: 'chat', title: 'Real people, any hour', text: 'Live chat is staffed around the clock, including weekends and holidays.' },
  { icon: 'heart', title: 'Patient, friendly help', text: 'Many of our customers are new to computers. We go at your pace and never rush.' },
  { icon: 'shield', title: 'Your data stays yours', text: 'We never ask for your password. We guide you — you stay in control of your account.' },
  { icon: 'book', title: 'Genealogy know-how', text: 'Our team understands trees, records, and DNA, not just browsers and apps.' },
];

/** Troubleshooting finder: each area lists symptoms and the quick fixes we try first. */
export const TROUBLE_AREAS = [
  {
    key: 'signin',
    icon: 'key',
    label: 'Sign-in & Account',
    issues: [
      {
        symptom: 'The password reset email never arrives',
        fixes: [
          'Check spam, junk, promotions, and “all mail” folders.',
          'Confirm you are using the same email address the account was created with — many people have an older address on file.',
          'Wait ten minutes before requesting another reset; multiple requests can cancel each other out.',
          'Add the sender to your contacts or safe-senders list, then request one new reset.',
        ],
      },
      {
        symptom: 'Verification code is rejected or never received',
        fixes: [
          'Make sure the phone number on the account is current and can receive texts.',
          'Use only the most recent code — earlier codes expire as soon as a new one is sent.',
          'Check that your device date and time are set automatically.',
          'Try the alternative verification method (email instead of text) if one is offered.',
        ],
      },
      {
        symptom: 'I think I have two accounts',
        fixes: [
          'Sign in with each email address you might have used and note which one holds your tree and DNA results.',
          'Do not delete either account until you know where everything lives.',
          'Trees can be shared or downloaded and re-uploaded; DNA results are tied to one account and need care to move.',
        ],
      },
    ],
  },
  {
    key: 'website',
    icon: 'globe',
    label: 'Website & Browser',
    issues: [
      {
        symptom: 'Pages are blank, half-loaded, or keep spinning',
        fixes: [
          'Refresh with Ctrl + F5 (Windows) or Cmd + Shift + R (Mac) to force a fresh copy.',
          'Clear the cache and cookies for the site only, then sign in again.',
          'Turn off ad blockers and privacy extensions for the site — they often block tree viewers and image loaders.',
          'Try a private window or a different browser to see if the problem follows you.',
        ],
      },
      {
        symptom: 'The tree viewer is extremely slow',
        fixes: [
          'Switch from the pedigree view to the family view, which loads fewer people at once.',
          'Close other tabs, especially ones playing video.',
          'Update your browser to the latest version and restart it.',
          'Very large trees (tens of thousands of people) load slower everywhere — a technician can suggest ways to work around this.',
        ],
      },
      {
        symptom: 'Record images will not open or zoom',
        fixes: [
          'Allow pop-ups for the site, as some image viewers open in a new window.',
          'Disable hardware acceleration in your browser settings if images appear black or distorted.',
          'Check that your internet connection is stable — large images can time out on slow connections.',
        ],
      },
    ],
  },
  {
    key: 'tree',
    icon: 'tree',
    label: 'Family Tree',
    issues: [
      {
        symptom: 'The same person appears twice',
        fixes: [
          'Compare both profiles side by side: dates, places, parents, and attached records.',
          'Use the merge option on the profile with the most information, and review every field before confirming.',
          'If you are unsure they are the same person, add a note instead of merging — merges are hard to undo.',
        ],
      },
      {
        symptom: 'A relationship is wrong (wrong parents, spouse, or child)',
        fixes: [
          'Open the person’s profile and choose to edit relationships rather than deleting the person.',
          'Remove the incorrect link, then attach them to the right family.',
          'Check attached records afterwards — a record accepted from a hint may have created the wrong link.',
        ],
      },
      {
        symptom: 'Hints are not appearing',
        fixes: [
          'Hints need names, dates, and places. Add at least a birth year and place.',
          'Check that the tree is not set to hide hints in its settings.',
          'New people can take a day or two before hints are generated.',
        ],
      },
    ],
  },
  {
    key: 'dna',
    icon: 'dna',
    label: 'DNA Kit & Results',
    issues: [
      {
        symptom: 'The kit activation code will not work',
        fixes: [
          'Check the code on the tube label carefully — letters such as O and I are often misread.',
          'Make sure you are signed in to the account that should own the results before activating.',
          'Each code can only be activated once. If it says “already activated”, check other family accounts.',
        ],
      },
      {
        symptom: 'Results are taking a long time',
        fixes: [
          'Processing commonly takes several weeks after the lab receives the sample. Busy holiday periods take longer.',
          'Check the status page in your account to see whether the sample has been received.',
          'If a sample cannot be processed, a replacement kit is usually offered — follow the instructions in your account.',
        ],
      },
      {
        symptom: 'My ethnicity estimate changed',
        fixes: [
          'Estimates are recalculated as reference panels grow. Changes reflect new science, not a problem with your sample.',
          'Your DNA matches are generally unaffected by an estimate update.',
          'Earlier estimates are not always kept — take a screenshot if you want a record of each version.',
        ],
      },
    ],
  },
  {
    key: 'app',
    icon: 'phone-app',
    label: 'Mobile App',
    issues: [
      {
        symptom: 'The app crashes or will not open after an update',
        fixes: [
          'Restart your phone or tablet completely.',
          'Install any pending app update and your device’s latest system update.',
          'On Android, clear the app’s cache from Settings › Apps. On iPhone, offload and reinstall the app.',
          'Your tree lives online, so reinstalling the app does not delete it.',
        ],
      },
      {
        symptom: 'Changes made on the phone do not show on the computer',
        fixes: [
          'Pull down to refresh in the app, and reload the page on the computer.',
          'Make sure you are signed in to the same account on both devices.',
          'Check that the phone has a working connection — edits made offline may wait to upload.',
        ],
      },
    ],
  },
  {
    key: 'billing',
    icon: 'card',
    label: 'Subscription',
    issues: [
      {
        symptom: 'I was charged but cannot see member content',
        fixes: [
          'Sign out completely and sign back in to refresh your membership status.',
          'Check whether the payment went to a different account (for example, an old email address).',
          'Look at the membership section of your account settings to confirm the plan and renewal date.',
        ],
      },
      {
        symptom: 'I want to change or cancel my plan',
        fixes: [
          'Changes and cancellations are made in the membership section of your account settings.',
          'Cancelling stops auto-renewal; you normally keep access until the end of the paid period.',
          'Your tree stays in your account even without a paid membership.',
        ],
      },
    ],
  },
];

/** Content for the Updates page. */
export const UPDATE_AREAS = [
  {
    icon: 'phone-app',
    title: 'Mobile app updates',
    text: 'The app is updated regularly with fixes and new features. Turn on automatic updates in the App Store or Google Play so you always have the latest version — many “bugs” are already fixed in the newest release.',
    checks: ['Enable automatic app updates', 'Keep your phone’s operating system current', 'Restart after big updates'],
  },
  {
    icon: 'globe',
    title: 'Browser updates',
    text: 'The website is built for current browsers. An outdated browser is one of the most common reasons trees load slowly, buttons do nothing, or images will not display.',
    checks: ['Use the latest Chrome, Edge, Firefox, or Safari', 'Restart the browser weekly', 'Review extensions after updates'],
  },
  {
    icon: 'dna',
    title: 'DNA estimate updates',
    text: 'Ethnicity estimates are refreshed from time to time as research improves. When an update rolls out, percentages and regions can shift. Your raw results and matches are not lost.',
    checks: ['Screenshot your current estimate', 'Read the update notes in your DNA page', 'Re-check matches for new connections'],
  },
  {
    icon: 'sync',
    title: 'Desktop tree software sync',
    text: 'If you keep a copy of your tree in desktop genealogy software, updates on either side can affect syncing. Update the desktop software first, back up, then sync.',
    checks: ['Back up before every sync', 'Update desktop software first', 'Resolve conflicts one at a time'],
  },
];

export const AFTER_UPDATE_CHECKLIST = [
  'Sign out and sign back in on every device you use.',
  'Open your home person’s profile and confirm facts and photos look correct.',
  'Check that recent edits appear on both phone and computer.',
  'Look at your notification and privacy settings — updates sometimes add new options.',
  'If something looks wrong, note what you see and when it started before changing anything.',
];

export const HOME_FAQS = [
  {
    q: `Is ${SITE_NAME} the official Ancestry helps team?`,
    a: `No. ${SITE_DOMAIN} is an independent helps service and is not affiliated with Ancestry.com. Ancestry offers its own help centre on its website. We provide a separate, independent option with patient one-to-one guidance.`,
  },
  {
    q: 'Will you ask for my password?',
    a: 'Never. We guide you through each step while you stay signed in on your own device. If anyone asks for your password, do not share it.',
  },
  {
    q: 'Can you help if I am not good with computers?',
    a: 'Absolutely — that is a big part of what we do. We explain everything in plain English and go at whatever pace suits you.',
  },
  {
    q: 'Do you help with DNA results?',
    a: 'Yes. We help with kit activation, linking results to the right person in your tree, understanding match lists, and what changes after an estimate update.',
  },
];

export const FAQ_GROUPS = [
  {
    id: 'service',
    title: 'About our service',
    items: [
      HOME_FAQS[0],
      HOME_FAQS[1],
      {
        q: 'How do I reach a technician?',
        a: 'Live chat is the fastest route and is open 24 hours a day. You can also use our contact form and we will reply, usually the same day.',
      },
      {
        q: 'Is there a charge?',
        a: 'Our guides and general advice in chat are free. Some in-depth one-to-one sessions may carry a fee, which is always explained and agreed before any work begins.',
      },
    ],
  },
  {
    id: 'accounts',
    title: 'Accounts & sign-in',
    items: [
      {
        q: 'Why do I keep getting signed out?',
        a: 'Usually because your browser is clearing cookies when it closes, or a privacy extension is blocking them. Allow cookies for the site and the problem normally stops.',
      },
      {
        q: 'Can I change the email address on my account?',
        a: 'Yes, from your account settings. Make sure you can receive email at the new address before switching, so you do not lock yourself out.',
      },
      {
        q: 'Someone else in my family manages my tree. Can I get access?',
        a: 'The tree owner can invite you and choose your role — viewer, contributor, or editor. Ask them to send an invitation to your email address.',
      },
    ],
  },
  {
    id: 'trees',
    title: 'Family trees',
    items: [
      {
        q: 'Can I download my family tree?',
        a: 'Yes. Trees can be exported as a GEDCOM file, a standard format most genealogy programs can open. Photos and attached media are not included in a GEDCOM and need to be saved separately.',
      },
      {
        q: 'Should I accept every hint?',
        a: 'No. Hints are suggestions. Check that names, dates, places, and family members genuinely match before accepting — wrong hints are the most common source of tree errors.',
      },
      {
        q: 'How do I make my tree private?',
        a: 'Open the tree settings and choose the privacy option. Private trees can still be shared with people you invite.',
      },
    ],
  },
  {
    id: 'dna',
    title: 'DNA',
    items: [
      HOME_FAQS[3],
      {
        q: 'Why did my match list change?',
        a: 'Matches can appear when new people test, and very small matches may be removed when matching methods are improved. Notes and groups on remaining matches are kept.',
      },
      {
        q: 'Can I download my raw DNA data?',
        a: 'Yes, from your DNA settings. The file contains sensitive information, so store it securely and only share it with services you trust.',
      },
    ],
  },
  {
    id: 'apps',
    title: 'Apps & updates',
    items: [
      {
        q: 'Does reinstalling the app delete my tree?',
        a: 'No. Your tree is stored online in your account. Reinstalling only removes the app’s local cache.',
      },
      {
        q: 'A feature disappeared after an update — what happened?',
        a: 'Features are sometimes moved to a different menu or renamed. Check the menus first, then ask us in live chat — we can usually show you where it went.',
      },
    ],
  },
];
